"use client";

import { cn } from "@/lib/utils";
import React, { useEffect, useState } from "react";
import { Plus } from "@/components/icons/plus-icon";
import { Minus } from "@/components/icons/minus-icon";

export interface StepperProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  size?: "lg" | "sm" | "auto";
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number;
  onValueChange?: (v: number) => void;
}

export default function Stepper({
  size = "lg",
  min = 0,
  max = 256,
  step = 1,
  defaultValue,
  onValueChange,
  ...props
}: StepperProps) {
  const { className, ...propsRest } = props;
  const [value, setValue] = useState(defaultValue || min);
  const handleIncrement: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    setValue(Math.min(max, value + step));
  };
  const handleDecrement: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    setValue(Math.max(min, value - step));
  };

  useEffect(() => {
    onValueChange?.(value);
  }, [onValueChange, value]);

  return (
    <div
      className={cn(
        size === "lg" && "gap-4",
        size === "sm" && "gap-2",
        size === "auto" && "gap-2 md:gap-4",
        "flex items-center",
        className
      )}
      {...propsRest}
    >
      <style>
        {`
          input[type=number]::-webkit-inner-spin-button, 
          input[type=number]::-webkit-outer-spin-button { 
            -webkit-appearance: none; 
            margin: 0; 
          }

          input[type=number] {
            -moz-appearance: textfield;
          }

          input[type=number]::-ms-clear {
            display: none;
          }

          input[type=number]::-ms-expand {
            display: none;
          }
        `}
      </style>
      <button
        className={cn(
          size === "lg" && "h-10 w-10",
          size === "sm" && "h-[26.67px] w-[26.67px]",
          size === "auto" && "h-[26.67px] w-[26.67px] md:h-10 md:w-10",
          "flex aspect-square items-center justify-center rounded-full bg-secondary-500 drop-shadow-md transition-colors duration-100 ease-out hover:bg-primary-200"
        )}
        aria-label="decrement"
        onClick={handleDecrement}
      >
        <Minus className="strokeWhite hover:stroke-secondary-500" />
      </button>
      <input
        className={cn(
          size === "lg" && "h-11 w-20 text-subheading-3-500",
          size === "sm" && "text-subheading-4-500 h-8 w-14",
          size === "auto" && "text-subheading-4-500 h-8 w-14 md:h-11 md:w-20",
          "rounded-[9px] border border-secondary-500 bg-transparent p-2 text-center font-montserrat text-neutral-600 focus:outline-none"
        )}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
      />
      <button
        className={cn(
          size === "lg" && "h-10 w-10",
          size === "sm" && "h-[26.67px] w-[26.67px]",
          size === "auto" && "h-[26.67px] w-[26.67px] md:h-10 md:w-10",
          "flex aspect-square items-center justify-center rounded-full bg-secondary-500 text-white drop-shadow-md transition-colors duration-100 ease-out hover:bg-primary-200"
        )}
        aria-label="increment"
        onClick={handleIncrement}
      >
        <Plus className="strokeWhite hover:stroke-secondary-500" />
      </button>
    </div>
  );
}
